import { getSyncStatus } from "@utils/api-communication/RestApiCli";

export interface SyncOutcome {
  status: "success" | "failed" | "unknown";
  failures?: Record<string, string>;
}

interface SyncStatusOutcome {
  status?: string;
  results?: Record<string, string>;
  failures?: Record<string, string>;
}

export function collectSyncFailures(
  lastOutcome: SyncStatusOutcome | undefined,
): Record<string, string> | undefined {
  const failures: Record<string, string> = {};
  Object.entries(lastOutcome?.results ?? {})
    .filter(([, status]) => status === "failed")
    .forEach(([section]) => {
      failures[section] = lastOutcome?.failures?.[section] ?? "";
    });
  Object.entries(lastOutcome?.failures ?? {}).forEach(([section, reason]) => {
    failures[section] = reason ?? "";
  });
  return Object.keys(failures).length > 0 ? failures : undefined;
}

const DEFAULT_POLL_INTERVAL_MS = 5000;
const MAX_POLL_INTERVAL_MS = 60000;
const MAX_CONSECUTIVE_ERRORS = 10;

export function createSyncPoller(intervalMs: number = DEFAULT_POLL_INTERVAL_MS) {
  const followers = new Set<(outcome: SyncOutcome) => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveErrors = 0;
  let generation = 0;
  let active = false;

  const stop = () => {
    generation += 1;
    active = false;
    if (timer) {
      clearTimeout(timer);
    }
    timer = null;
    consecutiveErrors = 0;
  };

  const complete = (outcome: SyncOutcome) => {
    const notified = Array.from(followers);
    followers.clear();
    stop();
    notified.forEach((follower) => follower(outcome));
  };

  const schedule = (delayMs: number) => {
    const scheduledGeneration = generation;
    timer = setTimeout(() => poll(scheduledGeneration), delayMs);
  };

  const poll = (pollGeneration: number) => {
    getSyncStatus(
      {},
      (response) => {
        if (pollGeneration !== generation) {
          return;
        }
        consecutiveErrors = 0;
        const data = response?.data;
        if (data?.inProgress) {
          schedule(intervalMs);
          return;
        }
        if (data?.lastOutcome?.status !== "failed") {
          complete({ status: "success" });
          return;
        }
        const failures = collectSyncFailures(data.lastOutcome);
        complete(failures ? { status: "failed", failures } : { status: "failed" });
      },
      () => {
        if (pollGeneration !== generation) {
          return;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          complete({ status: "unknown" });
          return;
        }
        schedule(Math.min(intervalMs * 2 ** consecutiveErrors, MAX_POLL_INTERVAL_MS));
      },
      null,
    );
  };

  return {
    follow(onComplete: (outcome: SyncOutcome) => void): () => void {
      followers.add(onComplete);
      if (!active) {
        active = true;
        schedule(intervalMs);
      }
      return () => {
        followers.delete(onComplete);
        if (followers.size === 0) {
          stop();
        }
      };
    },
  };
}

export const syncPoller = createSyncPoller();
